import { normalizeActivityFrame, normalizeResolution, redactPublicResolution } from './normalized-activity.js';
import { redactInteractionResponse } from '@borgee/agent-provider-sdk';
import { coalesceEntry } from '@borgee/agent-remote-web/headless';
const MAX_DETAIL_BYTES = 256 * 1024;
const MAX_DETAIL_ITEMS = 128;
const NOTICE_RESERVE_BYTES = 2048;
const CAPACITY_NOTICE = 'Some activity was removed or is unavailable because this task reached the local display capacity.';
/** Persist only a bounded local disposition when the original body cannot be admitted. */
export function unavailableSessionRecord(record, reason = CAPACITY_NOTICE) {
    const event = record.frame.event;
    return { ...record, resolution: undefined, presentation: { ...record.presentation, unavailable: true, reason: reason.slice(0, 512) }, frame: { ...record.frame, session_capabilities: undefined,
            event: { type: 'timeline', providerId: 'providerId' in event ? event.providerId : 'unknown', resources: [], item: { type: 'error', message: reason.slice(0, 512) } },
        } };
}
/** Snapshot requests become ordinary journal occurrences with independent response tokens. */
export function restoredInteractionFrames(frame, pending) {
    if (frame.event.type !== 'agent_snapshot' || frame.event.payload.status === 'closed' || frame.event.payload.runtimeInfo.status === 'closed')
        return [];
    const protocolVersion = frame.event.protocolVersion;
    const { session_control_binding_id: _binding, ...interactionFrame } = frame;
    return frame.event.payload.pendingInteractions
        .filter(request => !pending.some(value => value.request.requestId === request.requestId))
        .map(request => ({ ...interactionFrame, event: { protocolVersion, type: 'interaction_requested', payload: { agentId: frame.agent_id, request } } }));
}
/** Projection identity follows the first local record, never the changing end sequence. */
export class SessionProjector {
    requireCompleteState;
    constructor(requireCompleteState = false) {
        this.requireCompleteState = requireCompleteState;
    }
    providerId;
    runtimeInfo;
    nativeCapabilities;
    sessionControlBindingId;
    nativeSessionIdentity;
    nativeGeneration;
    turnId;
    startedAt;
    providers = new Map();
    groups = new Map();
    requests = new Map();
    currentGroupKey;
    failures = new Map();
    lifecycle = new Map();
    omissions = new Map();
    capacityNotice;
    authoritativeGenerations = new Set();
    fork() { return Object.assign(new SessionProjector(), structuredClone({ ...this })); }
    /** Replace Timeline indexes only; native state and pending response tokens are independent. */
    replaceTimeline(cut) {
        const versions = new Set(cut.records.map(record => record.frame.protocol_version));
        if (versions.size > 1)
            throw new Error('invalid_timeline_cut');
        const records = cut.records.map(record => {
            const frame = normalizeActivityFrame(record.frame);
            if (!frame || record.generation !== cut.generation || frame.event.type !== 'timeline')
                throw new Error('invalid_timeline_cut');
            return { ...record, frame };
        });
        this.authoritativeGenerations.add(cut.generation);
        for (const [key, group] of this.groups) {
            if (JSON.parse(key)[0] !== cut.generation)
                continue;
            group.entries = group.entries.filter(entry => group.confirmedOrdinals?.has(entry.seqStart));
            const retained = new Set(group.entries.map(entry => entry.seqStart));
            for (const ordinal of group.ids.keys())
                if (!retained.has(ordinal))
                    group.ids.delete(ordinal);
            group.toolIdentities.clear();
            for (const ordinal of group.interactionGenerations.keys())
                if (!retained.has(ordinal))
                    group.interactionGenerations.delete(ordinal);
        }
        for (const key of this.failures.keys()) {
            const [groupKey] = JSON.parse(key);
            if (JSON.parse(groupKey)[0] === cut.generation)
                this.failures.delete(key);
        }
        for (const record of records) {
            const event = record.frame.event;
            if (record.generation !== cut.generation || event.type !== 'timeline')
                throw new Error('invalid_timeline_cut');
            const key = JSON.stringify([cut.generation, record.frame.task_id]);
            let group = this.groups.get(key);
            if (!group) {
                group = { taskId: record.frame.task_id, entries: [], ids: new Map(), toolIdentities: new Map(), interactionGenerations: new Map() };
                this.groups.set(key, group);
            }
            this.providers.set(key, event.providerId);
            group.ids.set(record.ordinal, record.id);
            const entry = { ...this.entry(record, structuredClone(event.item)), providerId: event.providerId, resources: structuredClone(event.resources), ...(event.turnId ? { turnId: event.turnId } : {}) };
            group.entries = coalesceEntry(group.entries, entry);
            if (event.item.type === 'interaction')
                group.interactionGenerations.set(record.ordinal, record.interactionGeneration);
            if (event.item.type === 'tool_call' && this.nativeSessionIdentity)
                group.toolIdentities.set(record.ordinal, this.nativeSessionIdentity);
        }
    }
    append(record) {
        let frame = normalizeActivityFrame(record.frame);
        if (!frame)
            return record;
        record = { ...record, frame };
        const resolution = record.resolution ? normalizeResolution(record.resolution) : frame.event.type === 'interaction_resolved' ? frame.event : undefined;
        if (resolution) {
            const embedded = record.resolution && frame.event.type === 'interaction_requested' && frame.event.payload.request.requestId === resolution.payload.requestId ? frame.event.payload.request : undefined;
            const request = embedded ?? this.requests.get(this.requestKey(record, resolution.payload.requestId))?.request;
            const receipt = redactPublicResolution(resolution, request);
            if (record.resolution)
                record.resolution = receipt;
            else
                record.frame = frame = { ...frame, event: receipt };
        }
        if (record.presentation?.taskUnavailable)
            return this.rejectTask(record);
        const previousRuntime = this.runtimeInfo;
        const previousIdentity = this.nativeSessionIdentity;
        const { generation, ordinal } = record;
        for (const pending of this.requests.values()) {
            if (!record.resolution && pending.record.generation === generation && pending.record.interactionGeneration !== record.interactionGeneration)
                pending.expired = true;
        }
        const groupKey = JSON.stringify([generation, frame.task_id]);
        const incomingRuntimeIdentity = frame.event.type === 'runtime_updated'
            ? JSON.stringify([frame.event.providerId, frame.event.runtimeInfo.sessionId]) : undefined;
        const selectedSessionRuntime = frame.event.type === 'runtime_updated' && frame.task_id === ''
            && generation === this.nativeGeneration
            && frame.session_control_binding_id !== undefined
            && frame.session_control_binding_id === this.sessionControlBindingId
            && incomingRuntimeIdentity === this.nativeSessionIdentity;
        let group = this.groups.get(groupKey);
        if (!group) {
            group = { taskId: frame.task_id, entries: [], ids: new Map(), toolIdentities: new Map(), interactionGenerations: new Map() };
            this.groups.set(groupKey, group);
            const interaction = !!record.resolution || frame.event.type === 'interaction_requested' || frame.event.type === 'interaction_resolved';
            if (!selectedSessionRuntime && (!interaction || this.currentGroupKey === undefined || generation !== this.nativeGeneration || !this.lifecycle.get(generation)?.active)) {
                this.currentGroupKey = groupKey;
                if (this.nativeGeneration !== generation) {
                    this.sessionControlBindingId = undefined;
                    this.nativeCapabilities = undefined;
                    this.nativeSessionIdentity = undefined;
                    this.nativeGeneration = generation;
                    this.runtimeInfo = undefined;
                }
                group.nativeCapabilities = this.nativeCapabilities;
                this.turnId = undefined;
                this.startedAt = undefined;
                for (const previous of this.groups.values())
                    if (previous !== group) {
                        previous.active = false;
                        previous.terminal = true;
                    }
            }
        }
        group.ids.set(ordinal, record.id);
        if (record.presentation?.unavailable)
            return this.retain(record, previousRuntime);
        const event = (record.resolution ? normalizeResolution(record.resolution) : undefined) ?? frame.event;
        const terminal = event.type === 'turn_completed' || event.type === 'turn_failed' || event.type === 'turn_canceled';
        const selectedNativeSession = generation === this.nativeGeneration
            && frame.session_control_binding_id !== undefined && frame.session_control_binding_id === this.sessionControlBindingId
            && 'providerId' in event && event.providerId === this.providerId;
        const selectedTurnTerminal = selectedNativeSession && terminal && event.turnId !== undefined && event.turnId === this.turnId;
        // A restored Session turn and its terminal may have different Host message owners.
        if ((selectedNativeSession && event.type === 'turn_started') || selectedTurnTerminal) {
            if (selectedTurnTerminal && this.currentGroupKey !== groupKey) {
                const previous = this.groups.get(this.currentGroupKey ?? '');
                if (previous)
                    Object.assign(previous, { active: false, terminal: true, nativeTerminal: true });
            }
            this.currentGroupKey = groupKey;
        }
        const current = groupKey === this.currentGroupKey;
        if (event.type === 'agent_snapshot') {
            const snapshot = event.payload;
            const identity = JSON.stringify([snapshot.providerId, snapshot.runtimeInfo.sessionId]);
            group.nativeIdentity = snapshot.runtimeInfo.sessionId ? identity : undefined;
            const confirmed = new Set(snapshot.pendingInteractions.map(request => request.requestId));
            for (const [key, pending] of this.requests) {
                if (pending.nativeIdentity !== identity) {
                    pending.expired = true;
                    continue;
                }
                if (snapshot.status === 'closed' || snapshot.runtimeInfo.status === 'closed') {
                    pending.expired = true;
                    pending.nativeTerminal = true;
                }
                else if (confirmed.has(pending.request.requestId)) {
                    if (pending.record.generation !== generation || pending.record.interactionGeneration !== record.interactionGeneration || pending.expired)
                        this.requests.delete(key);
                    else
                        pending.expired = false;
                }
                else {
                    pending.expired = true;
                    pending.nativeTerminal = true;
                }
            }
            this.providerId = snapshot.providerId;
            this.providers.set(groupKey, snapshot.providerId);
            this.nativeSessionIdentity = identity;
            this.nativeGeneration = generation;
            this.nativeCapabilities = structuredClone(snapshot.capabilities);
            this.sessionControlBindingId = frame.session_control_binding_id;
            this.runtimeInfo = structuredClone(snapshot.runtimeInfo);
            this.turnId = snapshot.activeTurn?.turnId;
            this.startedAt = snapshot.activeTurn?.startedAt ?? undefined;
            const active = snapshot.activeTurn !== null && !['failed', 'closed'].includes(snapshot.status);
            this.lifecycle.set(generation, { taskId: frame.task_id, active });
            Object.assign(group, { runtimeInfo: this.runtimeInfo, nativeCapabilities: this.nativeCapabilities, turnId: this.turnId, active, terminal: !active, nativeTerminal: !active });
            return this.retain(record, previousRuntime);
        }
        if ('providerId' in event) {
            this.providers.set(groupKey, event.providerId);
            if (current)
                this.providerId = event.providerId;
        }
        const lifecycle = this.lifecycle.get(generation);
        const terminalTurnId = terminal ? event.turnId : undefined;
        const currentTurnTerminal = terminal && (selectedTurnTerminal || !terminalTurnId || !group.turnId || terminalTurnId === group.turnId);
        const runtimeTerminal = event.type === 'runtime_updated' && ['failed', 'closed'].includes(event.runtimeInfo.status);
        if ('turnId' in event && (!terminal || currentTurnTerminal))
            group.turnId = event.turnId;
        if (event.type === 'runtime_updated') {
            group.runtimeInfo = event.runtimeInfo;
            group.nativeIdentity = event.runtimeInfo.sessionId ? JSON.stringify([event.providerId, event.runtimeInfo.sessionId]) : undefined;
        }
        if (selectedSessionRuntime && event.type === 'runtime_updated') {
            this.providerId = event.providerId;
            if (frame.session_capabilities)
                this.nativeCapabilities = structuredClone(frame.session_capabilities);
            group.nativeCapabilities = this.nativeCapabilities;
            this.runtimeInfo = event.runtimeInfo;
        }
        if (event.type === 'turn_started') {
            group.active = true;
            group.terminal = false;
            group.nativeTerminal = false;
        }
        if (currentTurnTerminal || runtimeTerminal) {
            group.active = false;
            group.terminal = true;
            group.nativeTerminal = true;
        }
        if (current) {
            if ('turnId' in event && (!terminal || currentTurnTerminal))
                this.turnId = event.turnId;
            if (event.type === 'runtime_updated') {
                const identity = JSON.stringify([event.providerId, event.runtimeInfo.sessionId]);
                if (identity !== this.nativeSessionIdentity) {
                    this.nativeCapabilities = undefined;
                    for (const pending of this.requests.values())
                        if (pending.nativeIdentity && pending.nativeIdentity !== identity)
                            pending.expired = true;
                }
                this.nativeSessionIdentity = identity;
                this.sessionControlBindingId = frame.session_control_binding_id;
                if (frame.session_capabilities)
                    this.nativeCapabilities = structuredClone(frame.session_capabilities);
                group.nativeCapabilities = this.nativeCapabilities;
                this.runtimeInfo = event.runtimeInfo;
            }
            if (event.type === 'turn_started') {
                this.startedAt = new Date(frame.occurred_at ?? record.receivedAt).toISOString();
                this.runtimeInfo = { ...(this.runtimeInfo ?? { providerId: this.providerId ?? 'unknown', sessionId: null }), status: 'running' };
                this.lifecycle.set(generation, { taskId: frame.task_id, active: true });
            }
            else if (currentTurnTerminal) {
                this.lifecycle.set(generation, { taskId: frame.task_id, active: false });
                // A turn outcome cannot reopen a runtime that already reported its terminal state.
                const status = this.runtimeInfo?.status === 'closed' || this.runtimeInfo?.status === 'failed'
                    ? this.runtimeInfo.status : event.type === 'turn_failed' ? 'failed' : 'idle';
                this.runtimeInfo = { ...(this.runtimeInfo ?? { providerId: this.providerId ?? 'unknown', sessionId: null }), status };
            }
            else if (event.type === 'runtime_updated' && event.runtimeInfo.status === 'idle' && group.nativeTerminal !== false && lifecycle?.taskId === frame.task_id && lifecycle.active && ![...this.requests.values()].some(pending => pending.record.generation === generation && pending.record.frame.task_id === frame.task_id && !pending.expired)) {
                this.lifecycle.set(generation, { taskId: frame.task_id, active: false });
            }
            else if (event.type === 'runtime_updated' && (event.runtimeInfo.status === 'failed' || event.runtimeInfo.status === 'closed')) {
                this.lifecycle.set(generation, { taskId: frame.task_id, active: false });
            }
            else if ((event.type === 'timeline' || event.type === 'interaction_requested') && (!lifecycle || lifecycle.taskId !== frame.task_id)) {
                this.lifecycle.set(generation, { taskId: frame.task_id, active: true });
            }
        }
        if (selectedSessionRuntime && event.type === 'runtime_updated' && event.runtimeInfo.status === 'idle') {
            const activeLifecycle = this.lifecycle.get(generation);
            const activeGroupKey = activeLifecycle ? JSON.stringify([generation, activeLifecycle.taskId]) : undefined;
            const activeGroup = activeGroupKey !== undefined && activeGroupKey === this.currentGroupKey ? this.groups.get(activeGroupKey) : undefined;
            const hasPending = activeLifecycle && [...this.requests.values()].some(pending => pending.record.generation === generation
                && pending.record.frame.task_id === activeLifecycle.taskId && !pending.expired);
            if (activeLifecycle?.active && activeGroup && activeGroup.nativeTerminal !== false && !hasPending) {
                this.lifecycle.set(generation, { taskId: activeLifecycle.taskId, active: false });
                Object.assign(activeGroup, { active: false, terminal: true, nativeTerminal: true });
            }
        }
        if (current) {
            group.runtimeInfo = this.runtimeInfo;
            group.active = this.lifecycle.get(generation)?.active;
        }
        if (selectedSessionRuntime && runtimeTerminal) {
            this.lifecycle.set(generation, { taskId: this.lifecycle.get(generation)?.taskId ?? '', active: false });
            const activeGroup = this.groups.get(this.currentGroupKey ?? '');
            if (activeGroup) {
                activeGroup.active = false;
                activeGroup.terminal = true;
                activeGroup.nativeTerminal = true;
            }
            for (const pending of this.requests.values()) {
                if (event.type === 'runtime_updated' && event.runtimeInfo.status === 'closed' && pending.record.generation === generation) {
                    pending.expired = true;
                    pending.nativeTerminal = true;
                }
            }
        }
        if (event.type === 'turn_failed') {
            const failureKey = JSON.stringify([groupKey, event.turnId]);
            if (!this.failures.has(failureKey)) {
                this.failures.set(failureKey, event.error);
                if (!group.entries.some(entry => entry.turnId === event.turnId && entry.item.type === 'error' && entry.item.message === event.error)) {
                    group.entries.push({ ...this.entry(record, { type: 'error', message: event.error }), ...(event.turnId ? { turnId: event.turnId } : {}) });
                }
            }
        }
        if (event.type === 'interaction_requested') {
            const key = this.requestKey(record, event.payload.request.requestId);
            const previous = this.requests.get(key);
            if (previous?.expired) {
                this.requests.delete(key);
                this.requests.set(JSON.stringify([key, previous.record.id]), { ...previous, expired: true });
            }
            if (!this.requests.has(key) && !(record.interactionGeneration === undefined && group.entries.some(entry => entry.item.type === 'interaction' && entry.item.request.requestId === event.payload.request.requestId)))
                this.requests.set(key, { record, request: event.payload.request, expired: generation !== this.nativeGeneration || this.runtimeInfo?.status === 'closed', nativeIdentity: this.nativeSessionIdentity ?? previousIdentity });
        }
        else if (event.type === 'interaction_resolved') {
            const key = this.requestKey(record, event.payload.requestId);
            const pending = this.requests.get(key);
            if (record.resolution && frame.event.type === 'interaction_requested' && frame.event.payload.request.kind === event.payload.response.kind) {
                const request = frame.event.payload.request;
                if (!record.requestToken || pending?.record.id === record.requestToken)
                    this.requests.delete(key);
                const existing = record.requestToken && group.entries.find(entry => entry.item.type === 'interaction' && group.ids.get(entry.seqStart) === record.requestToken);
                if (existing) {
                    existing.item = { type: 'interaction', request, response: redactInteractionResponse(request, event.payload.response) };
                    (group.confirmedOrdinals ??= new Set()).add(existing.seqStart);
                    return this.retain(record, previousRuntime);
                }
                if (!group.confirmedOrdinals?.has(record.ordinal)) {
                    (group.confirmedOrdinals ??= new Set()).add(record.ordinal);
                    group.interactionGenerations.set(record.ordinal, record.interactionGeneration);
                    group.entries.push(this.entry(record, { type: 'interaction', request, response: redactInteractionResponse(request, event.payload.response) }));
                }
            }
            else if (pending && !pending.expired && pending.request.kind === event.payload.response.kind) {
                this.requests.delete(key);
                const requestGroup = this.groups.get(JSON.stringify([pending.record.generation, pending.record.frame.task_id]));
                requestGroup.ids.set(pending.record.ordinal, pending.record.id);
                requestGroup.interactionGenerations.set(pending.record.ordinal, pending.record.interactionGeneration);
                requestGroup.entries.push(this.entry(pending.record, { type: 'interaction', request: pending.request, response: redactInteractionResponse(pending.request, event.payload.response) }));
            }
        }
        else if (event.type === 'timeline') {
            if (event.item.type === 'error' && this.failures.get(JSON.stringify([groupKey, event.turnId])) === event.item.message)
                return record;
            if (event.item.type === 'interaction') {
                const item = event.item;
                this.requests.delete(this.requestKey(record, item.request.requestId));
                const existing = [...this.groups].filter(([key]) => JSON.parse(key)[0] === generation)
                    .flatMap(([, candidate]) => candidate.entries.filter(entry => candidate.interactionGenerations.get(entry.seqStart) === record.interactionGeneration))
                    .find(entry => entry.item.type === 'interaction' && entry.item.request.requestId === item.request.requestId);
                if (existing) {
                    existing.item = structuredClone(item);
                    return this.retain(record, previousRuntime);
                }
            }
            const incoming = {
                ...this.entry(record, event.item), providerId: event.providerId, resources: event.resources,
                ...(event.turnId ? { turnId: event.turnId } : {}),
            };
            if (event.item.type === 'tool_call' && event.turnId && group.nativeIdentity) {
                const callId = event.item.callId;
                const matches = [...this.groups.values()].filter(candidate => candidate !== group && candidate.taskId === frame.task_id)
                    .flatMap(candidate => candidate.entries.filter(entry => entry.item.type === 'tool_call' && entry.item.callId === callId
                    && entry.providerId === event.providerId && entry.turnId === event.turnId
                    && candidate.toolIdentities.get(entry.seqStart) === group.nativeIdentity).map(entry => ({ candidate, entry })));
                if (matches.length === 1) {
                    const { candidate, entry } = matches[0];
                    const merged = coalesceEntry([entry], incoming);
                    if (merged.length === 1) {
                        candidate.entries[candidate.entries.indexOf(entry)] = merged[0];
                        return this.retain(record, previousRuntime);
                    }
                }
            }
            const previousStarts = new Set(group.entries.map(entry => entry.seqStart));
            group.entries = coalesceEntry(group.entries, incoming);
            if (event.item.type === 'interaction')
                group.interactionGenerations.set(incoming.seqStart, record.interactionGeneration);
            if (event.item.type === 'tool_call' && group.nativeIdentity) {
                for (const entry of group.entries)
                    if (!previousStarts.has(entry.seqStart))
                        group.toolIdentities.set(entry.seqStart, group.nativeIdentity);
            }
        }
        if (event.type === 'runtime_updated' && event.runtimeInfo.status === 'closed') {
            for (const pending of this.requests.values()) {
                if (pending.record.generation === generation && (!pending.nativeIdentity || pending.nativeIdentity === group.nativeIdentity)) {
                    pending.expired = true;
                    pending.nativeTerminal = true;
                }
            }
        }
        return this.retain(record, previousRuntime);
    }
    view(liveGeneration) {
        const entries = [];
        const pending = [];
        const archivedInteractions = [];
        for (const group of this.groups.values()) {
            for (const entry of group.entries)
                entries.push({ id: group.ids.get(entry.seqStart), taskId: group.taskId, entry });
        }
        entries.push(...this.omissions.values());
        for (const { record, request, expired } of this.requests.values()) {
            if (!expired && record.generation === liveGeneration)
                pending.push({ taskId: record.frame.task_id, request, token: record.id, sequence: record.ordinal, timestamp: new Date(record.frame.occurred_at ?? record.receivedAt).toISOString() });
            else
                archivedInteractions.push({ id: record.id, taskId: record.frame.task_id, seqStart: record.ordinal, request });
        }
        return { sessionControlBindingId: liveGeneration === this.nativeGeneration ? this.sessionControlBindingId : undefined, nativeCapabilities: this.nativeCapabilities, nativeCapabilitiesLive: !!this.nativeCapabilities && liveGeneration !== undefined && liveGeneration === this.nativeGeneration, startedAt: this.startedAt, providerId: this.providerId, runtimeInfo: this.runtimeInfo, turnId: this.turnId, entries: entries.sort((a, b) => a.entry.seqStart - b.entry.seqStart), pending, archivedInteractions, active: liveGeneration ? this.lifecycle.get(liveGeneration)?.active ?? false : false, capacityNotice: this.capacityNotice };
    }
    taskIds() { return [...new Set([...this.groups.values()].map(group => group.taskId))]; }
    hasRequests(taskId) { return [...this.requests.values()].some(value => value.record.frame.task_id === taskId && !value.nativeTerminal); }
    pendingRecord(token) { return [...this.requests.values()].find(value => value.record.id === token)?.record; }
    taskSnapshot(liveGeneration) {
        const tasks = {};
        for (const [key, group] of this.groups) {
            if (!group.taskId)
                continue;
            const task = tasks[group.taskId] ??= { entries: [], pendingInteractions: [] };
            task.entries.push(...group.entries);
            task.providerId = this.providers.get(key) ?? task.providerId;
            task.runtimeInfo = group.runtimeInfo;
            task.nativeCapabilities = this.nativeCapabilities;
            task.nativeCapabilitiesLive = !!this.nativeCapabilities && liveGeneration === this.nativeGeneration && key === JSON.stringify([liveGeneration, group.taskId]);
            task.turnId = group.turnId;
            task.active = key === this.currentGroupKey && group.active === true && key === JSON.stringify([liveGeneration, group.taskId]);
            task.terminal = group.terminal;
        }
        for (const value of this.omissions.values())
            tasks[value.taskId]?.entries.push(value.entry);
        for (const { record, request, expired } of this.requests.values()) {
            const task = tasks[record.frame.task_id];
            if (task)
                task.pendingInteractions.push({ request, sequence: record.frame.sequence, displaySequence: record.ordinal,
                    timestamp: new Date(record.frame.occurred_at ?? record.receivedAt).toISOString(), expired: expired === true || record.generation !== liveGeneration, token: record.id });
        }
        for (const task of Object.values(tasks))
            task.entries.sort((a, b) => a.seqStart - b.seqStart);
        return tasks;
    }
    removeTask(taskId) {
        if (this.hasRequests(taskId))
            return;
        for (const [key, value] of this.requests)
            if (value.record.frame.task_id === taskId)
                this.requests.delete(key);
        for (const [key, group] of this.groups)
            if (group.taskId === taskId) {
                this.groups.delete(key);
                this.providers.delete(key);
            }
        for (const [key, value] of this.lifecycle)
            if (value.taskId === taskId)
                this.lifecycle.delete(key);
        for (const key of this.failures.keys()) {
            const [groupKey] = JSON.parse(key);
            if (!this.groups.has(groupKey))
                this.failures.delete(key);
        }
        this.omissions.delete(taskId);
    }
    rejectTask(record) {
        this.capacityNotice = 'New task activity is unavailable because earlier requests occupy the local display capacity.';
        return { ...this.unavailableRecord(record), presentation: { unavailable: true, taskUnavailable: true } };
    }
    retain(record, previousRuntime) {
        if (this.authoritativeGenerations.has(record.generation))
            return record;
        const taskId = record.frame.task_id;
        let trimmed = record.presentation?.trimmed === true;
        const groups = [...this.groups.values()].filter(group => group.taskId === taskId);
        const entries = () => groups.flatMap(group => group.entries).sort((a, b) => a.seqStart - b.seqStart);
        const requests = () => [...this.requests.values()].filter(value => value.record.frame.task_id === taskId);
        const tooLarge = () => {
            const view = this.view(record.generation);
            const task = { ...view, entries: view.entries.filter(value => value.taskId === taskId), pending: view.pending.filter(value => value.taskId === taskId), archivedInteractions: view.archivedInteractions.filter(value => value.taskId === taskId) };
            const bookkeeping = { groups: [...this.groups].filter(([, group]) => group.taskId === taskId).map(([key, group]) => [key, [...group.ids], group.turnId, group.active, group.terminal, group.nativeIdentity, [...group.toolIdentities], [...group.interactionGenerations]]),
                requests: requests().map(value => {
                    const { event, ...route } = value.record.frame;
                    const envelope = event.type === 'interaction_requested' ? { ...event, payload: { agentId: event.payload.agentId } } : {};
                    return { ...value.record, frame: { ...route, event: envelope }, expired: value.expired, nativeTerminal: value.nativeTerminal, nativeIdentity: value.nativeIdentity };
                }) };
            const metadataBytes = new TextEncoder().encode(JSON.stringify(bookkeeping)).byteLength;
            return task.entries.length + task.pending.length + task.archivedInteractions.length > MAX_DETAIL_ITEMS - 1
                || metadataBytes + Math.max(new TextEncoder().encode(JSON.stringify(task)).byteLength, new TextEncoder().encode(JSON.stringify(this.taskSnapshot(record.generation)[taskId])).byteLength) > MAX_DETAIL_BYTES - NOTICE_RESERVE_BYTES;
        };
        for (const group of groups) {
            const retained = new Set(group.entries.map(entry => entry.seqStart));
            for (const key of group.ids.keys())
                if (!retained.has(key))
                    group.ids.delete(key);
            for (const key of group.toolIdentities.keys())
                if (!retained.has(key))
                    group.toolIdentities.delete(key);
            for (const key of group.confirmedOrdinals ?? [])
                if (!retained.has(key))
                    group.confirmedOrdinals.delete(key);
            for (const key of group.interactionGenerations.keys())
                if (!retained.has(key))
                    group.interactionGenerations.delete(key);
        }
        let omitted = record.presentation?.unavailable === true || trimmed;
        while (tooLarge()) {
            const oldest = entries()[0];
            const historical = requests().filter(value => value.nativeTerminal).sort((a, b) => a.record.ordinal - b.record.ordinal)[0];
            if (!oldest && !historical)
                break;
            if (historical && (!oldest || historical.record.ordinal < oldest.seqStart)) {
                for (const [key, value] of this.requests)
                    if (value === historical)
                        this.requests.delete(key);
            }
            else {
                for (const group of groups)
                    group.entries = group.entries.filter(entry => entry !== oldest);
            }
            trimmed = true;
            omitted = true;
        }
        if (tooLarge()) {
            if (this.requireCompleteState)
                throw new Error('session_projection_capacity');
            const added = requests().find(value => value.record.id === record.id);
            if (added) {
                for (const [key, value] of this.requests)
                    if (value === added)
                        this.requests.delete(key);
                record = this.unavailableRecord(record);
            }
            this.runtimeInfo = previousRuntime;
            const currentGroup = this.groups.get(this.currentGroupKey ?? "");
            if (currentGroup?.taskId === taskId)
                currentGroup.runtimeInfo = previousRuntime;
            omitted = true;
        }
        if (omitted) {
            this.omissions.set(taskId, { id: `capacity:${taskId}`, taskId, entry: this.entry(record, { type: 'error', message: record.presentation?.reason?.slice(0, 512) ?? CAPACITY_NOTICE }) });
            record = { ...record, presentation: { ...record.presentation, ...(trimmed ? { trimmed: true } : {}) } };
        }
        for (const group of groups) {
            const retained = new Set(group.entries.map(entry => entry.seqStart));
            for (const value of requests())
                if (value.record.generation === record.generation)
                    retained.add(value.record.ordinal);
            for (const key of group.ids.keys())
                if (!retained.has(key))
                    group.ids.delete(key);
            for (const key of group.toolIdentities.keys())
                if (!retained.has(key))
                    group.toolIdentities.delete(key);
            for (const key of group.confirmedOrdinals ?? [])
                if (!retained.has(key))
                    group.confirmedOrdinals.delete(key);
            for (const key of group.interactionGenerations.keys())
                if (!retained.has(key))
                    group.interactionGenerations.delete(key);
        }
        const retainedTurns = new Set(groups.flatMap(group => group.entries).map(entry => JSON.stringify(entry.turnId)));
        for (const key of this.failures.keys()) {
            const [groupKey, turnId] = JSON.parse(key);
            const [, failureTask] = JSON.parse(groupKey);
            if (failureTask === taskId && !retainedTurns.has(JSON.stringify(turnId)))
                this.failures.delete(key);
        }
        for (const [key, group] of this.groups) {
            if (key === this.currentGroupKey || group.taskId !== taskId)
                continue;
            const pending = [...this.requests.values()].some(value => JSON.stringify([value.record.generation, value.record.frame.task_id]) === key);
            if (!pending && group.entries.length === 0) {
                this.groups.delete(key);
                this.providers.delete(key);
            }
            else if (this.groups.get(this.currentGroupKey ?? '')?.taskId === taskId)
                group.runtimeInfo = undefined;
        }
        for (const generation of this.lifecycle.keys())
            if (![...this.groups.keys()].some(key => JSON.parse(key)[0] === generation))
                this.lifecycle.delete(generation);
        return record;
    }
    requestKey(record, requestId) {
        return JSON.stringify([record.generation, record.interactionGeneration, requestId]);
    }
    unavailableRecord(record) {
        return unavailableSessionRecord(record);
    }
    entry(record, item) {
        return { providerId: this.providers.get(JSON.stringify([record.generation, record.frame.task_id])) ?? this.providerId ?? 'unknown', item, timestamp: new Date(record.frame.occurred_at ?? record.receivedAt).toISOString(), seqStart: record.ordinal, seqEnd: record.ordinal,
            sourceSeqRanges: [{ startSeq: record.frame.sequence, endSeq: record.frame.sequence }], collapsed: [], resources: [] };
    }
}
export function projectSession(records, liveGeneration) {
    const projector = new SessionProjector();
    for (const record of records)
        projector.append(record);
    return projector.view(liveGeneration);
}
//# sourceMappingURL=session-projection.js.map